"use client";

import log from "logbeacon";
import { useQuery } from "@tanstack/react-query";
import {
  OHLC,
  MarketPair,
  MarketPairPagination,
  type OpenDataClient,
} from "../features/api/openData";

import { useIsPc } from "./useIsPc";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { QUERY_KEYS } from "@config";

interface UseAgentMarketPairsOptions {
  /** 查询参数 */
  params: NonNullable<
    Parameters<OpenDataClient<unknown>["pairs"]["getMarketPairsForAgents"]>[0]
  >;
  /** 是否启用查询 */
  enabled?: boolean;
  /** 缓存时间（毫秒） */
  staleTime?: number;
}

const OFFSET = 0;
const LIMIT = 50;
/**
 * 获取代理市场交易对数据
 *
 * @param options 查询选项
 * @returns 查询结果和状态
 */
export const useAgentMarketPairs = (options: UseAgentMarketPairsOptions) => {
  const { openDataApi } = useOspApiClientProvider();
  const { params, enabled = true, staleTime } = options;
  const [isPc] = useIsPc();
  const { data, isLoading, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [QUERY_KEYS.AGENT_MARKET_PAIRS, params],
    enabled: !!openDataApi && enabled,
    staleTime,
    queryFn: async () => {
      log.debug("[文案]useAgentMarketPairs queryFn params:", params);

      const pairRequest: NonNullable<
        Parameters<
          OpenDataClient<unknown>["pairs"]["getMarketPairsForAgents"]
        >[0]
      > = {
        "enrich.enable": true,
        "enrich.with_ohlc": "LONG",
        "enrich.with_market.enable": true,
        offset: OFFSET,
        limit: isPc ? LIMIT : 20,
        ...params,
      };
      const res = await openDataApi?.pairs.getMarketPairsForAgents({
        ...pairRequest,
      });

      if (res?.error) {
        log.error("[文案]useAgentMarketPairs API error:", res.error);
        return Promise.reject(res.error);
      }

      log.debug("[文案]useAgentMarketPairs API response:", res?.data);
      return res?.data?.data as MarketPairPagination;
    },
    select: (responseData) => {
      const marketData = responseData?.rows?.map((item: MarketPair) => {
        return {
          ...item,
          price: item.market_data?.current_price,
          priceChange: item.market_data?.price_change_percentage_24h,
          ohlc: item?.ohlc?.map((item: OHLC) => {
            return {
              ...item,
              timestamp: item?.time || 0,
              open: Number(item.open),
              high: Number(item.high),
              low: Number(item.low),
              close: Number(item.close),
              volume: Number(item.volume),
            };
          }),
        };
      });
      const pairs = marketData?.map((item) => `${item.base}/${item.quote}`);
      return {
        marketData,
        pairs,
      };
    },
  });
  return {
    data,
    isLoading,
    isPending,
    isFetching,
    error,
    refetch,
  };
};
